import dayjs from '@/lib/dayjs'
import { DayjsProperty } from '@/lib/jackson'
import { formatValueStringToLabel } from '@/utils/helper'
import { Dayjs } from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

export type CreateGoalSettingBody = {
  memberId: number
  goalIds: number[]
  startDate: string
  endDate: string
  summary?: string
  detail?: string | null
  trainerId: number
}

export type EditGoalSettingBody = CreateGoalSettingBody & {
  notes: string
}

export type EditGoalSettingStatusBody = {
  status: string
  summary: string
  trainerId: number
}

export type EditSwimmingGoalSettingStatusBody = {
  status: string
  summary: string
}

export enum GoalSessionPhase {
  TRAINING_PLAN = 'TRAINING_PLAN',
  ACTUAL_RESULT = 'ACTUAL_RESULT',
}

export enum ExerciseCategory {
  STRENGTH_AND_CONDITIONING = 'STRENGTH_AND_CONDITIONING',
  HYPERTROPHY = 'HYPERTROPHY',
  CIRCUIT_TRAINING = 'CIRCUIT_TRAINING',
  MOBILITY_STABILITY = 'MOBILITY_STABILITY',
  CORE_BODY = 'CORE_BODY',
}

// Human-readable labels matching the backend ExerciseCategory enum mapping.
export const EXERCISE_CATEGORY_LABELS: Record<ExerciseCategory, string> = {
  [ExerciseCategory.STRENGTH_AND_CONDITIONING]: 'Strength and Conditioning',
  [ExerciseCategory.HYPERTROPHY]: 'Hypertrophy',
  [ExerciseCategory.CIRCUIT_TRAINING]: 'Circuit Training',
  [ExerciseCategory.MOBILITY_STABILITY]: 'Mobility/Stability',
  [ExerciseCategory.CORE_BODY]: 'Core Body',
}

export type SessionExerciseItemBody = {
  exerciseName: string
  weight?: number
  reps?: number
  /** Exercise-library reference — lets member surfaces resolve image/GIF + video. */
  exerciseId?: number
}

export type SessionExerciseFocusBody = {
  category: string
  muscle: string[]
  remark?: string
  exercises?: SessionExerciseItemBody[]
  imageUrl?: string[]
  videoUrl?: string[]
}

export type SessionBodyCompositionBody = {
  weight?: number
  fatPercent?: number
  muscleMass?: number
  strength?: number
  speed?: number
  flexibility?: number
  endurance?: number
  agility?: number
  remark?: string
  imageUrl?: string[]
  videoUrl?: string[]
}

export type CreateSessionGroupBody = {
  goalSettingId: number
}

export type CreateGroupLogBody = {
  sessionDate: string
  sessionPhase: GoalSessionPhase
  result?: string
  exerciseFocus?: SessionExerciseFocusBody
  bodyComposition?: SessionBodyCompositionBody
}

export type EditGroupLogBody = {
  sessionDate?: string
  result?: string
  exerciseFocus?: SessionExerciseFocusBody
  bodyComposition?: SessionBodyCompositionBody | null
}

export type SendSessionPlanBody = {
  logId: number
  sessionPhase: GoalSessionPhase
}

export type GoalSettingOption = {
  slug: string
  name: string
}

@Serializable()
export class GoalSetting extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('memberId')
  memberId!: number

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('memberNickname')
  memberNickname?: string | null

  @JsonProperty('memberPhone')
  memberPhone?: string | null

  @JsonProperty('goals')
  goals!: string[]

  @JsonProperty('status')
  status!: string | null

  @DayjsProperty()
  startDate!: Dayjs

  @DayjsProperty()
  endDate!: Dayjs

  @JsonProperty('summary')
  summary?: string

  @JsonProperty('detail')
  detail?: string

  @DayjsProperty()
  createdAt!: Dayjs

  @JsonProperty('trainingType')
  trainingType?: string
}

@Serializable()
export class Goal extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string
}

@Serializable()
export class SwimmingGoalSetting extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('memberId')
  memberId!: number

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('memberNickname')
  memberNickname?: string | null

  @JsonProperty('memberPhone')
  memberPhone?: string | null

  @JsonProperty('reason')
  reason?: string | null

  @JsonProperty('goal')
  goal!: SwimmingMemberType

  @JsonProperty('goalId')
  goalId!: number

  @JsonProperty('member')
  member!: SwimmingMemberDetails

  @JsonProperty('memberType')
  memberType!: string

  @JsonProperty('otherPersonalGoal')
  otherPersonalGoal?: string

  @JsonProperty('status')
  status!: string | null

  @DayjsProperty()
  startDate!: Dayjs

  @DayjsProperty()
  endDate!: Dayjs

  @JsonProperty('summary')
  summary?: string

  @JsonProperty('detail')
  detail?: string

  @DayjsProperty()
  createdAt!: Dayjs

  @DayjsProperty()
  updatedAt!: Dayjs

  @JsonProperty('updatedBy')
  updatedBy!: number

  @JsonProperty('updatedByUser')
  updatedByUser!: SwimmingMemberDetails

  public DataForTable() {
    const start = dayjs(this.startDate)
    const end = dayjs(this.endDate)
    const startLabel = start?.format('DD MMM YYYY') || ''
    const endLabel = end.isValid() ? end.format('DD MMM YYYY') : ''

    const periodRemaining = end.isBefore(dayjs())
      ? 0
      : dayjs().isAfter(start)
        ? dayjs(end).diff(dayjs(), 'day') + 1
        : dayjs(end).diff(dayjs(start), 'day') + 1
    return {
      id: this.id,
      period: `${startLabel} → ${endLabel}`,
      periodRemaining,
      trainingGoal: this.goal?.name || '-',
      status: this.status ? formatValueStringToLabel(this.status) : '-',
      statusColor:
        this.status === 'IN_TRAINING'
          ? 'error.main'
          : this.status === 'COMPLETED' || this.status === 'SUCCESS'
            ? 'success.main'
            : this.status === 'NOT_STARTED'
              ? 'secondary.dark'
              : 'primary.light',
      memberName: this.member.firstName + ' ' + this.member.lastName,
      memberNickname: this.member.nickname || '-',
      memberParticipant: this.memberType ? formatValueStringToLabel(this?.memberType) : '-',
      goalId: this.goalId,
      memberId: this.memberId,
    }
  }
}

@Serializable()
export class SwimmingMemberDetails extends Fromable {
  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('id')
  id!: number

  @JsonProperty('phone')
  phone?: string

  @JsonProperty('email')
  email?: string
}

@Serializable()
export class SwimmingMemberType extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string
}

@Serializable()
export class GoalSettingSessionExercise extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('exerciseName')
  exerciseName?: string | null

  @JsonProperty('weight')
  weight?: number | null

  @JsonProperty('reps')
  reps?: number | null

  /** Exercise-library reference — resolve media via GET /workout/exercises/:id. */
  @JsonProperty('exerciseId')
  exerciseId?: number | null
}

@Serializable()
export class GoalSettingSessionExerciseFocus extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('category')
  category?: string | null

  @JsonProperty('muscle')
  muscle?: string[] | null

  @JsonProperty('remark')
  remark?: string | null

  @JsonProperty('exercises')
  exercises?: GoalSettingSessionExercise[] | null

  @JsonProperty('imageUrl')
  imageUrl?: string[] | null

  @JsonProperty('videoUrl')
  videoUrl?: string[] | null
}

@Serializable()
export class GoalSettingSessionBodyComposition extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('weight')
  weight?: number | null

  @JsonProperty('fatPercent')
  fatPercent?: number | null

  @JsonProperty('muscleMass')
  muscleMass?: number | null

  @JsonProperty('strength')
  strength?: number | null

  @JsonProperty('speed')
  speed?: number | null

  @JsonProperty('flexibility')
  flexibility?: number | null

  @JsonProperty('endurance')
  endurance?: number | null

  @JsonProperty('agility')
  agility?: number | null

  @JsonProperty('remark')
  remark?: string | null

  @JsonProperty('imageUrl')
  imageUrl?: string[] | null

  @JsonProperty('videoUrl')
  videoUrl?: string[] | null
}

@Serializable()
export class GoalSettingSession extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('goalSettingId')
  goalSettingId!: number

  @DayjsProperty()
  sessionDate?: Dayjs | null

  @JsonProperty('sessionNo')
  sessionNo?: number | null

  @JsonProperty('result')
  result?: string | null

  @JsonProperty('exerciseFocus')
  exerciseFocus?: GoalSettingSessionExerciseFocus | null

  @JsonProperty('bodyComposition')
  bodyComposition?: GoalSettingSessionBodyComposition | null

  @JsonProperty()
  exerciseCategory?: string

  @JsonProperty('videoUrl')
  videoUrl?: string[] | null

  @JsonProperty('createdBy')
  createdBy?: {
    id: number
    firstName: string
    lastName: string
    nickname?: string | null
  }

  @JsonProperty('trainer')
  trainer?: {
    id: number
    firstName: string
    lastName: string
    nickname: string
  }

  @DayjsProperty()
  createdAt?: Dayjs | null

  @DayjsProperty()
  updatedAt?: Dayjs | null

  public DataForTable() {
    return {
      id: this.id,
      sessionDate: this.sessionDate?.format('DD MMM YYYY') || '-',
      sessionNo: this.sessionNo || '-',
      trainerName: this.trainer?.firstName + ' ' + this.trainer?.lastName || '-',
      exerciseCategory: this.exerciseCategory || '-',
    }
  }
}

@Serializable()
export class GoalSettingEditLogUser extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname?: string | null
}

@Serializable()
export class GoalSettingEditLog extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('goalSettingId')
  goalSettingId!: number

  @JsonProperty('goalType')
  goalType!: string[]

  @JsonProperty('trainerId')
  trainerId!: number

  @JsonProperty({ path: 'trainer', type: GoalSettingEditLogUser })
  trainer?: GoalSettingEditLogUser | null

  @JsonProperty({ path: 'editedByUser', type: GoalSettingEditLogUser })
  editedByUser?: GoalSettingEditLogUser | null

  @DayjsProperty()
  startDate?: Dayjs | null

  @DayjsProperty()
  endDate?: Dayjs | null

  @JsonProperty('noted')
  noted?: string | null

  @JsonProperty('detail')
  detail?: string | null

  @JsonProperty('editedBy')
  editedBy?: number | null

  @DayjsProperty()
  createdAt!: Dayjs
}

@Serializable()
export class SwimmingGoalSettingEditLogResponse extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('swimmingGoalSettingId')
  swimmingGoalSettingId!: number

  @JsonProperty('otherPersonalGoal')
  otherPersonalGoal?: string

  @JsonProperty('goalId')
  goalId?: number

  @JsonProperty('trainerId')
  trainerId!: number

  @JsonProperty({ path: 'trainer', type: GoalSettingEditLogUser })
  trainer?: GoalSettingEditLogUser | null

  @JsonProperty({ path: 'editedByUser', type: GoalSettingEditLogUser })
  editedByUser?: GoalSettingEditLogUser | null

  @DayjsProperty()
  startDate?: Dayjs | null

  @DayjsProperty()
  endDate?: Dayjs | null

  @JsonProperty('noted')
  noted?: string | null

  @JsonProperty('editedBy')
  editedBy?: number | null

  @DayjsProperty()
  createdAt!: Dayjs

  @JsonProperty('detail')
  detail?: string | null

  @JsonProperty('reason')
  reason?: string | null
}

@Serializable()
export class SessionGroup extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('sessionNo')
  sessionNo!: number

  @DayjsProperty()
  sessionDate!: Dayjs

  @JsonProperty('status')
  status!: string

  @JsonProperty('trainerName')
  trainerName!: string

  @JsonProperty('exerciseCategory')
  exerciseCategory!: string
}

@Serializable()
export class GoalSessionGroupCreated extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('goalSettingId')
  goalSettingId?: number

  @JsonProperty('sessionNo')
  sessionNo?: number
}

@Serializable()
export class PersonalGoalSession extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('goalSettingId')
  goalSettingId!: number

  @JsonProperty('goalSessionGroupId')
  goalSessionGroupId!: number

  @DayjsProperty()
  sessionDate?: Dayjs | null

  @JsonProperty('sessionPhase')
  sessionPhase?: string | null

  @JsonProperty({ path: 'exerciseFocus', type: GoalSettingSessionExerciseFocus })
  exerciseFocus?: GoalSettingSessionExerciseFocus | null

  @JsonProperty({ path: 'bodyComposition', type: GoalSettingSessionBodyComposition })
  bodyComposition?: GoalSettingSessionBodyComposition | null

  @JsonProperty({ path: 'createdBy', type: GoalSettingEditLogUser })
  createdBy?: GoalSettingEditLogUser | null

  @DayjsProperty()
  createdAt?: Dayjs | null

  @DayjsProperty()
  updatedAt?: Dayjs | null
}

@Serializable()
export class PersonalGoalSettingSession extends Fromable {
  @JsonProperty({ path: 'trainingPlan', type: PersonalGoalSession })
  trainingPlan?: PersonalGoalSession | null

  @JsonProperty({ path: 'actualResult', type: PersonalGoalSession })
  actualResult?: PersonalGoalSession | null
}

@Serializable()
export class GoalSettingWithSession extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('memberId')
  memberId!: number

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('memberNickname')
  memberNickname?: string | null

  @JsonProperty('memberPhone')
  memberPhone?: string | null

  @JsonProperty('goals')
  goals!: string[]

  @JsonProperty('status')
  status!: string | null

  @DayjsProperty()
  startDate!: Dayjs

  @DayjsProperty()
  endDate!: Dayjs

  @JsonProperty('detail')
  detail?: string

  @DayjsProperty()
  createdAt!: Dayjs

  @JsonProperty({ path: 'sessionGroups', elementType: SessionGroup })
  sessionGroups?: SessionGroup[]
}
